import React, { useState, useEffect } from 'react';
import { getRandomWord, resetUsedWords } from '../data/wordDictionary';

const WordPuzzleMaster = () => {
  const [gameState, setGameState] = useState('menu');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [currentWordData, setCurrentWordData] = useState(null);
  const [scrambledLetters, setScrambledLetters] = useState([]);
  const [selectedLetters, setSelectedLetters] = useState([]);
  const [timeLeft, setTimeLeft] = useState(90);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [streak, setStreak] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [showMeaning, setShowMeaning] = useState(false);
  const [completedWordData, setCompletedWordData] = useState(null);

  const scrambleWord = (word) => {
    const letters = word.split('');
    let attempts = 0;
    let scrambled;
    
    do {
      scrambled = [...letters];
      for (let i = scrambled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [scrambled[i], scrambled[j]] = [scrambled[j], scrambled[i]];
      }
      attempts++;
    } while (scrambled.join('') === word && attempts < 50 && word.length > 2);
    
    return scrambled.map((letter, index) => ({ letter, id: index, used: false }));
  };

  const startGame = () => {
    resetUsedWords();
    setGameState('playing');
    setScore(0);
    setLevel(1);
    setTimeLeft(90);
    setWordsCompleted(0);
    setStreak(0);
    setSelectedLetters([]);
    setShowHint(false);
    setShowMeaning(false);
    setCompletedWordData(null);
    loadNewWord(1);
  };

  const loadNewWord = (lvl) => {
    const wordData = getRandomWord(Math.min(lvl, 8));
    setCurrentWordData(wordData);
    setScrambledLetters(scrambleWord(wordData.word));
    setSelectedLetters([]);
    setShowHint(false);
  };

  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(t => {
          if (t <= 1) {
            setGameState('gameover');
            return 0;
          }
          return t - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [gameState, timeLeft]);

  const selectLetter = (letterObj) => {
    if (letterObj.used) return;
    
    const newScrambled = scrambledLetters.map(l => 
      l.id === letterObj.id ? { ...l, used: true } : l
    );
    setScrambledLetters(newScrambled);
    setSelectedLetters([...selectedLetters, letterObj]);
  };

  const removeLetter = (index) => {
    const letterToRemove = selectedLetters[index];
    const newScrambled = scrambledLetters.map(l =>
      l.id === letterToRemove.id ? { ...l, used: false } : l
    );
    setScrambledLetters(newScrambled);
    setSelectedLetters(selectedLetters.filter((_, i) => i !== index));
  };

  const checkWord = () => {
    const formedWord = selectedLetters.map(l => l.letter).join('');
    
    if (formedWord === currentWordData.word) {
      const points = currentWordData.word.length * 10 * level;
      setScore(s => s + points + (streak * 20));
      setStreak(s => s + 1);
      setWordsCompleted(w => w + 1);
      setTimeLeft(t => t + 5);
      
      // Show meaning before moving to next word
      setCompletedWordData(currentWordData);
      setShowMeaning(true);
      
      // Check for level up
      if (wordsCompleted > 0 && (wordsCompleted + 1) % 5 === 0) {
        setLevel(l => Math.min(l + 1, 8));
      }
    } else {
      setStreak(0);
      // Reset selection
      setScrambledLetters(scrambleWord(currentWordData.word));
      setSelectedLetters([]);
    }
  };

  const continueToNextWord = () => {
    setShowMeaning(false);
    setCompletedWordData(null);
    loadNewWord(level);
  };

  const shuffleLetters = () => {
    setScrambledLetters(scrambleWord(currentWordData.word));
    setSelectedLetters([]);
  };

  const skipWord = () => {
    setStreak(0);
    loadNewWord(level);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100 to-blue-100 p-8">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-5xl font-bold text-center mb-8 text-green-800">
          🔤 Word Puzzle Master 🧩
        </h1>

        {gameState === 'menu' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🔤</div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Unscramble the Words!</h2>
            <p className="text-xl mb-8 text-gray-600">
              Match letters to create words<br/>
              Difficulty increases with each level<br/>
              Unlimited words to solve!
            </p>
            <div className="bg-green-50 p-6 rounded-lg mb-8">
              <h3 className="font-bold text-lg mb-3">Game Features:</h3>
              <div className="grid grid-cols-2 gap-4 text-left">
                <div>✓ 8 difficulty levels</div>
                <div>✓ Unlimited words</div>
                <div>✓ Streak bonuses</div>
                <div>✓ Time bonuses</div>
              </div>
            </div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              START PUZZLE
            </button>
          </div>
        )}

        {gameState === 'playing' && !showMeaning && currentWordData && (
          <div>
            <div className="bg-white rounded-lg p-4 mb-4 flex justify-between items-center flex-wrap gap-2">
              <div className="text-xl font-bold">Score: {score}</div>
              <div className="text-xl font-bold">Level: {level}</div>
              <div className="text-xl font-bold">Words: {wordsCompleted}</div>
              {streak > 1 && (
                <div className="text-xl font-bold text-orange-600 animate-pulse">
                  🔥 Streak: {streak}x
                </div>
              )}
              <div className={`text-xl font-bold ${timeLeft < 20 ? 'text-red-600 animate-pulse' : 'text-green-600'}`}>
                Time: {timeLeft}s
              </div>
            </div>

            <div className="bg-white rounded-lg p-8 shadow-xl mb-4">
              <div className="text-center mb-6">
                <div className="text-3xl font-bold text-gray-700 mb-2">
                  Word Length: {currentWordData.word.length} letters
                </div>
                <div className="text-lg text-gray-500 mb-3">
                  Level {level} - {
                    level <= 2 ? 'Beginner' :
                    level <= 4 ? 'Intermediate' :
                    level <= 6 ? 'Advanced' :
                    'Expert'
                  }
                </div>
                {/* Hint Button */}
                <button
                  onClick={() => setShowHint(!showHint)}
                  className="bg-yellow-400 text-gray-800 px-4 py-2 rounded-lg font-bold hover:bg-yellow-500 transition"
                >
                  {showHint ? '🔒 Hide Hint' : '💡 Show Hint'}
                </button>
                {showHint && (
                  <div className="mt-3 bg-yellow-50 p-4 rounded-lg border-2 border-yellow-400">
                    <div className="text-lg font-bold text-yellow-800">
                      💡 Hint: {currentWordData.hint}
                    </div>
                  </div>
                )}
              </div>

              {/* Selected Letters Area */}
              <div className="bg-blue-50 rounded-lg p-6 mb-6 min-h-24">
                <div className="text-lg font-bold text-gray-700 mb-3 text-center">Your Word:</div>
                <div className="flex justify-center gap-2 flex-wrap">
                  {selectedLetters.length === 0 ? (
                    <div className="text-gray-400 text-lg">Click letters below to form a word</div>
                  ) : (
                    selectedLetters.map((letterObj, index) => (
                      <button
                        key={index}
                        onClick={() => removeLetter(index)}
                        className={`bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-600 transition transform hover:scale-110 ${
                          level <= 2 ? 'w-16 h-16 text-3xl' :
                          level <= 4 ? 'w-14 h-14 text-2xl' :
                          level <= 6 ? 'w-12 h-12 text-xl' :
                          'w-10 h-10 text-lg'
                        }`}
                      >
                        {letterObj.letter}
                      </button>
                    ))
                  )}
                </div>
              </div>

              {/* Scrambled Letters */}
              <div className="mb-6">
                <div className="text-lg font-bold text-gray-700 mb-3 text-center">Available Letters:</div>
                <div className="flex justify-center gap-2 flex-wrap">
                  {scrambledLetters.map((letterObj) => (
                    <button
                      key={letterObj.id}
                      onClick={() => selectLetter(letterObj)}
                      disabled={letterObj.used}
                      className={`font-bold rounded-lg transition transform hover:scale-110 ${
                        level <= 2 ? 'w-16 h-16 text-3xl' :
                        level <= 4 ? 'w-14 h-14 text-2xl' :
                        level <= 6 ? 'w-12 h-12 text-xl' :
                        'w-10 h-10 text-lg'
                      } ${
                        letterObj.used
                          ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                          : 'bg-green-500 text-white hover:bg-green-600'
                      }`}
                    >
                      {letterObj.letter}
                    </button>
                  ))}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={shuffleLetters}
                  className="bg-yellow-500 text-white py-3 rounded-lg font-bold text-lg hover:bg-yellow-600 transition"
                >
                  🔄 Shuffle
                </button>
                <button
                  onClick={skipWord}
                  className="bg-gray-500 text-white py-3 rounded-lg font-bold text-lg hover:bg-gray-600 transition"
                >
                  ⏭️ Skip
                </button>
                <button
                  onClick={checkWord}
                  disabled={selectedLetters.length !== currentWordData.word.length}
                  className={`py-3 rounded-lg font-bold text-lg transition ${
                    selectedLetters.length === currentWordData.word.length
                      ? 'bg-green-500 text-white hover:bg-green-600'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  ✓ Check
                </button>
              </div>
            </div>

            {streak > 2 && (
              <div className="text-center text-2xl font-bold text-orange-600 animate-bounce">
                🔥 Amazing {streak}x Streak! Keep going! 🔥
              </div>
            )}
          </div>
        )}

        {/* Word Meaning Display */}
        {gameState === 'playing' && showMeaning && completedWordData && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🎉</div>
            <h2 className="text-4xl font-bold mb-4 text-green-600">Correct!</h2>
            <div className="text-5xl font-bold mb-6 text-gray-800">
              {completedWordData.word}
            </div>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <div className="text-xl font-bold text-gray-700 mb-3">Meaning:</div>
              <div className="text-2xl text-gray-600">
                {completedWordData.meaning}
              </div>
            </div>
            <div className="text-xl mb-6 text-green-600 font-bold">
              +{completedWordData.word.length * 10 * level} points!
            </div>
            <button
              onClick={continueToNextWord}
              className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              Next Word →
            </button>
          </div>
        )}

        {gameState === 'gameover' && (
          <div className="bg-white rounded-lg shadow-2xl p-12 text-center">
            <div className="text-6xl mb-6">🏆</div>
            <h2 className="text-4xl font-bold mb-4 text-gray-800">Puzzle Complete!</h2>
            <div className="text-3xl mb-4 text-green-600 font-bold">Final Score: {score}</div>
            <div className="text-2xl mb-2 text-gray-600">Words Completed: {wordsCompleted}</div>
            <div className="text-2xl mb-8 text-gray-600">Max Streak: {streak}x</div>
            <button
              onClick={startGame}
              className="bg-gradient-to-r from-green-500 to-blue-600 text-white px-12 py-4 rounded-full text-2xl font-bold hover:scale-110 transform transition"
            >
              PLAY AGAIN
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default WordPuzzleMaster;
